/**
 * Task categories by industry (organization industryType).
 * Used for task creation dropdown; merge with org's customTaskCategories for full list.
 * See TASK_CATEGORIES_BY_INDUSTRY.md for descriptions.
 */

const DEFAULT_CATEGORIES = [
  'Operational',
  'Safety',
  'Maintenance',
] as const;

/** Industry-specific task category lists. Key = industryType (must match org.industryType). */
export const TASK_CATEGORIES_BY_INDUSTRY: Record<string, readonly string[]> = {
  'Accommodation and Food Services': [
    'Operational',
    'Safety',
    'Maintenance',
    'Food Safety & Sanitation',
    'Guest Experience',
    'Kitchen / Back of House',
    'Housekeeping & Cleaning',
    'Compliance & Licensing',
    'Security',
    'Events & Banquets',
  ],
  'Construction': [
    'Operational',
    'Safety',
    'Maintenance',
    'Quality & Inspection',
    'Subcontractor / Vendor',
    'Permits & Compliance',
    'Environmental',
    'Site Security',
  ],
  'Real Estate and Property Management': [
    'Operational',
    'Safety',
    'Maintenance',
    'Tenant / Resident',
    'Leasing & Marketing',
    'Compliance & Licensing',
    'Vendor & Contractor',
    'Security',
  ],
  'Healthcare and Social Assistance': [
    'Operational',
    'Safety',
    'Maintenance',
    'Patient Care / Clinical',
    'Infection Control & Sanitation',
    'Compliance & Licensing',
    'Environmental',
    'Security',
  ],
  'Manufacturing': [
    'Operational',
    'Safety',
    'Maintenance',
    'Quality',
    'Production',
    'Environmental',
    'Compliance & Licensing',
    'Security',
  ],
  'Retail and Wholesale Trade': [
    'Operational',
    'Safety',
    'Maintenance',
    'Inventory & Stock',
    'Customer Service',
    'Compliance & Licensing',
    'Loss Prevention / Security',
  ],
  'Transportation and Logistics': [
    'Operational',
    'Safety',
    'Maintenance',
    'Fleet / Vehicle',
    'Compliance & Licensing',
    'Environmental',
    'Security',
  ],
  'Education': [
    'Operational',
    'Safety',
    'Maintenance',
    'Student / Instructional',
    'Compliance & Licensing',
    'Facilities & Grounds',
    'Security',
  ],
  'Professional, Scientific, and Technical Services': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Licensing',
    'Client Delivery',
    'Security',
  ],
  'Energy and Utilities': [
    'Operational',
    'Safety',
    'Maintenance',
    'Environmental',
    'Compliance & Licensing',
    'Security',
  ],
  'Environmental Services': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Licensing',
    'Field / Site',
    'Security',
  ],
  'Finance and Insurance': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Regulatory',
    'Risk',
    'Security',
  ],
  'Bank': [
    'Operational',
    'Compliance & Regulatory',
    'Risk',
    'Loan Operations',
    'Security',
  ],
  'Information Technology and Telecommunications': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Licensing',
    'Security',
  ],
  'Arts, Entertainment, and Recreation': [
    'Operational',
    'Safety',
    'Maintenance',
    'Guest / Patron Experience',
    'Compliance & Licensing',
    'Security',
  ],
  'Public Administration and Government': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Regulatory',
    'Security',
  ],
  'Nonprofit Organizations': [
    'Operational',
    'Safety',
    'Maintenance',
    'Program Delivery',
    'Compliance & Licensing',
    'Security',
  ],
  'Agriculture, Forestry, Fishing, and Hunting': [
    'Operational',
    'Safety',
    'Maintenance',
    'Environmental',
    'Compliance & Licensing',
    'Security',
  ],
  'Mining, Quarrying, and Oil & Gas Extraction': [
    'Operational',
    'Safety',
    'Maintenance',
    'Environmental',
    'Compliance & Licensing',
    'Security',
  ],
  'Administrative and Support Services': [
    'Operational',
    'Safety',
    'Maintenance',
    'Compliance & Licensing',
    'Client Service',
    'Security',
  ],
  'Waste Management and Remediation Services': [
    'Operational',
    'Safety',
    'Maintenance',
    'Environmental',
    'Compliance & Licensing',
    'Security',
  ],
};

/** Default list when industry is unknown or not in map. */
export function getDefaultTaskCategories(): readonly string[] {
  return DEFAULT_CATEGORIES;
}

/**
 * Get industry default task categories for an industry type.
 * Case-insensitive match on industryType; falls back to default.
 */
export function getTaskCategoriesForIndustry(industryType: string | null | undefined): string[] {
  if (!industryType || !industryType.trim()) {
    return [...DEFAULT_CATEGORIES];
  }
  const exact = TASK_CATEGORIES_BY_INDUSTRY[industryType];
  if (exact) return [...exact];
  const lower = industryType.toLowerCase();
  const key = Object.keys(TASK_CATEGORIES_BY_INDUSTRY).find((k) => k.toLowerCase() === lower);
  if (key) return [...TASK_CATEGORIES_BY_INDUSTRY[key]];
  return [...DEFAULT_CATEGORIES];
}

/**
 * Merge industry defaults with custom org categories. Dedupes by normalized label (trim, case-insensitive).
 * Order: industry list first, then custom (custom appended after).
 */
export function mergeTaskCategories(
  industryCategories: string[],
  customCategories: string[] | null | undefined
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const label of industryCategories) {
    const n = label.trim();
    if (!n) continue;
    const key = n.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(n);
  }
  if (customCategories && Array.isArray(customCategories)) {
    for (const label of customCategories) {
      const n = (typeof label === 'string' ? label : '').trim();
      if (!n) continue;
      const key = n.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(n);
    }
  }
  return result;
}
